// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { StorefrontAccess } from "@insite/client-framework/Services/SettingsService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getCategoryDepthLoaded as actualGetCategoryDepthLoaded,
    getCategoryState,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { HasLinksState } from "@insite/client-framework/Store/Links/LinksState";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import React, { useMemo } from "react";
import { shallowEqual } from "react-redux";

export const getHomePageUrl = (state: HasLinksState) => {
    return getPageLinkByPageType(state, "HomePage")?.url ?? "/";
};

export const getPageLinkByNodeId = (state: HasLinksState, nodeId: string) => {
    const path = state.links.nodeIdToPageLinkPath[nodeId];
    if (!path) {
        return;
    }

    let result = state.links.pageLinks[path[0]];
    for (let index = 1; index < path.length; index = index + 1) {
        if (result.children) {
            result = result.children[path[index]];
        }
    }

    if (result.type === "OrderUploadPage" || result.type === "QuickOrderPage") {
        const applicationState = state as ApplicationState;
        if (applicationState?.context?.settings != null) {
            const settings = getSettingsCollection(applicationState);
            const productSettings = settings.productSettings;
            if (
                (productSettings.storefrontAccess === StorefrontAccess.SignInRequiredToAddToCart ||
                    productSettings.storefrontAccess === StorefrontAccess.SignInRequiredToAddToCartOrSeePrices) &&
                !applicationState.context.session.isAuthenticated
            ) {
                return;
            }
        }
    }

    return result;
};

export const getPageLinkByPageType = (state: HasLinksState, pageType: string) => {
    const nodeId = state.links?.pageTypesToNodeId[pageType];
    if (!nodeId) {
        return;
    }

    return getPageLinkByNodeId(state, nodeId);
};

type LinkItem = { url?: string; title?: string };
const frozenEmptyLink = Object.freeze<LinkItem>({ url: undefined, title: undefined });
const linksMemoDictionary: SafeDictionary<LinkItem> = {};

/**
 * @deprecated Use the `useGetLink` hook instead to ensure category links get loaded.
 */
export const getLink = (state: ApplicationState, { type, value }: LinkFieldValue): LinkItem => {
    if (type === "Page") {
        return getPageLinkByNodeId(state, value) || frozenEmptyLink;
    }

    if (type === "Category") {
        const category = getCategoryState(state, value).value;

        if (!category) {
            return frozenEmptyLink;
        }

        const key = category.path + category.shortDescription;
        if (!linksMemoDictionary[key]) {
            linksMemoDictionary[key] = {
                url: category.path,
                title: category.shortDescription,
            };
        }

        return linksMemoDictionary[key];
    }

    if (type === "Url") {
        if (!linksMemoDictionary[value]) {
            linksMemoDictionary[value] = {
                url: value,
                title: value,
            };
        }

        return linksMemoDictionary[value];
    }

    return frozenEmptyLink;
};

export const useGetLinks = <T>(list: T[], linkSelector: (item: T) => LinkFieldValue) => {
    const linkFields = React.useMemo(() => list.map(linkSelector), [list, linkSelector]);

    const links = useAppSelector(state => linkFields.map(o => getLink(state, o)), shallowEqual);

    const categoryStates = useAppSelector(
        state =>
            linkFields.map(linkField => {
                if (linkField.type !== "Category") {
                    return undefined;
                }

                return getCategoryState(state, linkField.value);
            }),
        shallowEqual,
    );

    const dispatch = useAppDispatch();

    React.useEffect(() => {
        for (let x = 0; x < links.length; x++) {
            const linkField = linkFields[x];
            const link = links[x];
            const categoryState = categoryStates[x];

            if (
                linkField.type === "Category" &&
                linkField.value &&
                !link.url &&
                !categoryState?.isLoading &&
                !categoryState?.errorStatusCode
            ) {
                dispatch(loadCategory({ id: linkField.value }));
            }
        }
    }, [links, linkFields, categoryStates, dispatch]);

    return links;
};

export const useGetLink = (linkField: LinkFieldValue) => {
    const defaultLinkField = useMemo(() => ({ type: "Page", value: "" }), []);
    linkField = linkField || defaultLinkField;
    return useGetLinks([linkField], o => o)[0];
};

export interface LinkModel {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
    };
}

/**
 * @deprecated Use the `useGetLinks` hook instead to ensure category links get loaded. Mapping can be done calling component
 */
export function mapLinks<L extends LinkModel, R = {}>(
    state: ApplicationState,
    links?: L[],
    optionalReturnFunction?: (widgetLink: L, stateLink?: ReturnType<typeof getLink>) => R,
) {
    if (!links || links.length < 1) {
        return [];
    }
    return links.map(widgetLink => {
        const stateLink = getLink(state, widgetLink.fields.destination);
        const optionalReturnValues =
            typeof optionalReturnFunction === "function" ? optionalReturnFunction(widgetLink, stateLink) : {};
        return {
            url: stateLink?.url,
            title: stateLink?.title,
            ...optionalReturnValues,
        } as ReturnType<typeof getLink> & R;
    });
}

/**
 * @deprecated Use getCategoryState instead
 */
export function getCategoryLink(state: HasLinksState, categoryId: string) {
    return getCategoryState(state as ApplicationState, categoryId).value;
}

/**
 * @deprecated Use @insite/client-framework/Store/Data/Categories/CategoriesSelectors/getCategoryDepthLoaded instead
 */
export function getCategoryDepthLoaded(state: HasLinksState, categoryId: string) {
    return actualGetCategoryDepthLoaded(state as ApplicationState, categoryId);
}
